import Vue from 'vue';
import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import Rect from '../../rect.js';
import Point from '../../point.js';
import { unwrapMention, setMentionContent } from '../project/mention-helper.js';
import updater from '../project/updater.js';
import { has, roundToNearestMultiple } from '../../utils.js';

/**
 *  A store for handling the application clipboard. Non persisting store.
 */
export const TYPE = Object.freeze({
  COMPONENTS: 'COMPONENTS',
  ELEMENTS: 'ELEMENTS',
});

export const OPERATION = Object.freeze({
  COPY: 'COPY',
  CUT: 'CUT',
});

const state = {
  data: {
    elements: [],
    attributes: [],
    notes: [],
    jumpers: [],
    branches: [],
    connections: [],
    comments: [],
    commentChildren: [],
  },
  type: TYPE.ELEMENTS,
  operation: OPERATION.COPY,
  hash: null,
};

const getters = {
  /**
   * Returns the full object data in the clipboard
   * @returns {Object}    The full clipboard data structure
   */
  getClipboard(state, getters) {
    return state;
  },

  /**
   * Returns whether the clipboard has data or not.
   * @returns {Boolean}   Whether the clipboard is empty
   */
  getIsEmpty(state) {
    const { elements, notes, jumpers, branches, connections, comments } =
      state.data;
    return (
      elements.length === 0 &&
      notes.length === 0 &&
      jumpers.length === 0 &&
      branches.length === 0 &&
      connections.length === 0 &&
      comments.length === 0
    );
  },

  /**
   * Returns whether the paste operation can be made
   * @returns {Boolean}   Whether the paste is allowed
   */
  getCanPaste(state, getters) {
    return !getters.getIsEmpty;
  },
};

const actions = {
  /**
   * Performs the copy operation. Finds the selected elements, notes and connections and puts them in the clipboard
   */
  copy(context, { operation = OPERATION.COPY } = {}) {
    // Get the selected elements-connections
    const elementIds =
      context.rootGetters['project/config/elements/getSelections'];
    const connectionIds =
      context.rootGetters['project/config/connections/getSelections'];
    const noteIds = context.rootGetters['project/config/notes/getSelections'];
    const jumperIds =
      context.rootGetters['project/config/jumpers/getSelections'];
    const branchIds =
      context.rootGetters['project/config/branches/getSelections'];
    const commentIds =
      context.rootGetters['project/config/comments/getSelections'];

    // Get all jumpers
    const jumpers = _.cloneDeep(
      context.rootGetters['project/data/jumpers/getNodes'](jumperIds)
    );

    // Get all branches
    const branches = _.cloneDeep(
      context.rootGetters['project/data/branches/getNodes'](branchIds)
    );

    // Get all related conditions
    const conditionIds = branchIds.flatMap(branchId =>
      context.rootGetters['project/data/branches/getConditions'](branchId)
    );
    const conditions = _.cloneDeep(
      context.rootGetters['project/data/conditions/getNodes'](conditionIds)
    );

    // Get all elements
    const elements = _.cloneDeep(
      context.rootGetters['project/data/elements/getNodes'](elementIds)
    );

    // Get all related attributes
    const attributeIds = elements.flatMap(element => {
      return Array.isArray(element.attributes) ? element.attributes : [];
    });
    const attributes = _.cloneDeep(
      context.rootGetters['project/data/attributes/getNodes'](attributeIds)
    );

    // Get all connections
    const connections = _.cloneDeep(
      context.rootGetters['project/data/connections/getNodes'](connectionIds)
    );

    // Get all notes
    const notes = _.cloneDeep(
      context.rootGetters['project/data/notes/getNodes'](noteIds)
    );

    // Get all comments
    let comments = [];
    let commentChildren = [];
    if (operation === OPERATION.CUT) {
      comments = _.cloneDeep(
        context.rootGetters['project/data/comments/getNodes'](commentIds)
      );
      const childrenIds = [];
      comments
        .filter(c => c.children)
        .forEach(c => {
          childrenIds.push(...c.children);
        });
      commentChildren = _.cloneDeep(
        context.rootGetters['project/data/comments/getNodes'](childrenIds)
      );
    }

    // Get all references to elements from jumpers that are outside the selection
    const jumperReferences = {};
    elementIds.forEach(id => {
      const elementJumpers =
        context.rootGetters['project/data/jumpers/getElementJumpers'](id);
      elementJumpers
        .filter(jid => !jumperIds.includes(jid))
        .forEach(jid => {
          jumperReferences[jid] = id;
        });
    });

    // Get the starting element (if it is selected)
    const startingElement =
      context.rootGetters['project/data/elements/getStartingElement'];

    // Get the active project
    const hash = context.rootGetters['user/projects/getOpen'];

    // Copy the actual store data to the clipboard
    context.commit('setClipboard', {
      data: {
        elements,
        attributes,
        notes,
        jumpers,
        branches,
        conditions,
        connections,
        comments,
        commentChildren,
        jumperReferences,
        startingElement,
      },
      type: TYPE.ELEMENTS,
      operation,
      hash,
    });
  },

  /**
   * Performs the cut operation. Finds the selected elements, notes and connections, puts them in the clipboard and removes them.
   */
  cut(context) {
    // Copy the selected content to clipboard
    context.dispatch('copy', { operation: OPERATION.CUT });
    // Delete all selected content
    context.dispatch(
      'project/config/deleteSelections',
      { apply: true },
      { root: true }
    );
  },

  /**
   * Performs the paste operation. Finds the selected elements, notes and connections in the clipboard and puts them on board.
   *
   * @param {Object}  atPos The position (board coordinates) for where to paste
   */
  async paste(context, { atPos }) {
    console.log('Pasting content...');
    // Deselect everything (just in case there is something selected) - we will select pasted content later
    context.dispatch(
      'project/config/deselectAll',
      { apply: false },
      { root: true }
    );
    // Create deep copies of elements and connections stored in the clipboard
    const clipboard = context.getters.getClipboard;
    const elements = _.cloneDeep(clipboard.data.elements);
    const attributes = _.cloneDeep(clipboard.data.attributes);
    const jumpers = _.cloneDeep(clipboard.data.jumpers);
    const branches = _.cloneDeep(clipboard.data.branches);
    const conditions = _.cloneDeep(clipboard.data.conditions);
    let connections = _.cloneDeep(clipboard.data.connections);
    const notes = _.cloneDeep(clipboard.data.notes);
    const comments = _.cloneDeep(clipboard.data.comments);
    const commentChildren = _.cloneDeep(clipboard.data.commentChildren);
    const jumperReferences = _.cloneDeep(clipboard.data.jumperReferences);
    const { startingElement } = clipboard.data;
    const isSameProject =
      clipboard.hash === context.rootGetters['user/projects/getOpen'];

    // Compute the elements,jumpers and notes bounding rectangle
    const elementsRect =
      context.rootGetters['project/data/elements/getBoundingRect'](elements);

    // We set fixed width & height for jumpers since we do not store their dimensions
    const jumpersRect = Rect.boundingRect(
      jumpers.map(
        jumper => new Rect(jumper.x, jumper.x + 200, jumper.y, jumper.y + 81)
      )
    );

    // We set fixed width & height for branches since we do not store their dimensions
    const branchesRect = Rect.boundingRect(
      branches.map(
        branch => new Rect(branch.x, branch.x + 260, branch.y, branch.y + 110)
      )
    );

    // We set the fixed width and height for the comment pins
    const commentsRect = Rect.boundingRect(
      comments.map(
        comment =>
          new Rect(
            comment.position.x,
            comment.position.x + 22,
            comment.position.y,
            comment.position.y + 20
          )
      )
    );

    const notesRect =
      context.rootGetters['project/data/notes/getBoundingRect'](notes);

    const rects = [];
    if (elementsRect) {
      rects.push(elementsRect);
    }
    if (notesRect) {
      rects.push(notesRect);
    }
    if (jumpersRect) {
      rects.push(jumpersRect);
    }
    if (branchesRect) {
      rects.push(branchesRect);
    }
    if (commentsRect) {
      rects.push(commentsRect);
    }

    console.log('Various rects comprising the selection:', rects);

    // In case no elements,jumpers or notes in the copied data do nothing
    if (rects.length === 0) {
      context.dispatch('empty', {});
      return;
    }

    // Get the bounding rectangle of all rect types
    const rect = Rect.boundingRect(rects);

    // Check if we are snapping
    const { snap, size } = context.rootGetters['project/config/getGrid'];

    // Restrain the point so that we will not leave the board edges and add/remove a snap cell size
    const pt = new Point(atPos.x, atPos.y);
    let tempX = Math.max(pt.x, rect.width / 2);
    let tempY = Math.max(pt.y, rect.height / 2);

    if (snap) {
      if (tempX !== pt.x) {
        pt.x = tempX + size;
      }
      if (tempY !== pt.y) {
        pt.y = tempY + size;
      }
    } else {
      pt.x = tempX;
      pt.y = tempY;
    }

    tempX = Math.min(pt.x, 20000 - rect.width / 2);
    tempY = Math.min(pt.y, 20000 - rect.height / 2);

    if (snap) {
      if (tempX !== pt.x) {
        pt.x = tempX - size;
      }
      if (tempY !== pt.y) {
        pt.y = tempY - size;
      }
    } else {
      pt.x = tempX;
      pt.y = tempY;
    }

    // Compute the difference of rect's midpoint from the given point
    const diff = rect.midpoint.differenceFromPoint(pt);

    const boardId = context.rootGetters['project/config/boards/getOpen'];
    const hash = {};

    // Parse all elements
    const elementIds = [];
    const elementNodes = elements.map(element => {
      const id = uuidv4();
      hash[element.id] = id;
      element.id = id;
      element.x -= diff.x;
      element.y -= diff.y;
      // Adjust x, y when snapping to grid
      if (snap) {
        element.x = roundToNearestMultiple(element.x, size);
        element.y = roundToNearestMultiple(element.y, size);
      }
      // element.outputs = []; // temporarily
      // In case we paste on different project remove attached components and mentions on components-boards
      if (!isSameProject) {
        element.components = [];
        element.content = unwrapMention(element.content, null, 'component');
        element.content = unwrapMention(element.content, null, 'board');
        element.title = unwrapMention(element.title, null, 'component');
        element.title = unwrapMention(element.title, null, 'board');
        if (element.assets) {
          delete element.assets;
        }
      }
      elementIds.push(element.id);
      return element;
    });

    const attributeIds = [];
    const attributeNodes = attributes.map(attribute => {
      const id = uuidv4();
      hash[attribute.id] = id;
      attribute.id = id;
      attribute.cId = hash[attribute.cId];
      if (!isSameProject) {
        if (attribute.value.type === 'component-list') {
          attribute.value.data = [];
        } else if (
          attribute.value.type === 'string' &&
          !attribute.value.plain
        ) {
          attribute.value.data = unwrapMention(
            attribute.value.data,
            null,
            'component'
          );
          attribute.value.data = unwrapMention(
            attribute.value.data,
            null,
            'board'
          );
        }
      }
      attributeIds.push(attribute.id);
      return attribute;
    });

    // Parse all jumpers
    const jumperIds = [];
    const jumperNodes = jumpers.map(jumper => {
      const id = uuidv4();
      hash[jumper.id] = id;
      jumper.id = id;
      jumper.x -= diff.x;
      jumper.y -= diff.y;
      // Adjust x, y when snapping to grid
      if (snap) {
        jumper.x = roundToNearestMultiple(jumper.x, size);
        jumper.y = roundToNearestMultiple(jumper.y, size);
      }
      // In case we paste on different project remove attached element from jumper
      if (!isSameProject) {
        jumper.elementId = null;
      } else if (hash[jumper.elementId]) {
        // In case the attached element is also in the clipboard, link to the new one
        jumper.elementId = hash[jumper.elementId];
      }
      jumperIds.push(jumper.id);
      return jumper;
    });

    // Parse all conditions
    const conditionIds = [];
    const conditionNodes = conditions.map(condition => {
      const id = uuidv4();
      hash[condition.id] = id;
      condition.id = id;
      conditionIds.push(condition.id);
      return condition;
    });

    // Parse all branches
    const branchIds = [];
    const branchNodes = branches.map(branch => {
      const id = uuidv4();
      hash[branch.id] = id;
      branch.id = id;
      branch.x -= diff.x;
      branch.y -= diff.y;
      // Adjust x, y when snapping to grid
      if (snap) {
        branch.x = roundToNearestMultiple(branch.x, size);
        branch.y = roundToNearestMultiple(branch.y, size);
      }
      // Parse the branch conditions
      const { ifCondition, elseIfConditions, elseCondition } =
        branch.conditions;

      if (ifCondition) {
        branch.conditions.ifCondition = hash[ifCondition];
      }

      if (elseIfConditions) {
        branch.conditions.elseIfConditions = elseIfConditions.map(
          elseIfCondition => hash[elseIfCondition]
        );
      }

      if (elseCondition) {
        branch.conditions.elseCondition = hash[elseCondition];
      }

      branchIds.push(branch.id);
      return branch;
    });

    // Parse all notes
    const noteIds = [];
    const noteNodes = notes.map(note => {
      const id = uuidv4();
      hash[note.id] = id;
      note.id = id;
      note.x -= diff.x;
      note.y -= diff.y;
      // Adjust x, y when snapping to grid
      if (snap) {
        note.x = roundToNearestMultiple(note.x, size);
        note.y = roundToNearestMultiple(note.y, size);
      }
      // In case we paste on different project remove attached components and mentions
      if (!isSameProject) {
        note.content = unwrapMention(note.content, null, 'component');
        note.content = unwrapMention(note.content, null, 'board');
      }
      noteIds.push(note.id);
      return note;
    });

    // Filter out connections that do not connect two copied elements
    connections = connections.filter(
      (
        connection // filter out irrelevant connections
      ) => has(hash, connection.sourceid) && has(hash, connection.targetid)
    );

    const connectionIds = [];
    const connectionNodes = connections.map(connection => {
      const id = uuidv4();
      hash[connection.id] = id;
      connection.id = id;
      connection.sourceid = hash[connection.sourceid];
      connection.targetid = hash[connection.targetid];
      // In case we paste on different project remove attached components and mentions
      if (!isSameProject) {
        connection.label = unwrapMention(connection.label, null, 'component');
        connection.label = unwrapMention(connection.label, null, 'board');
      }
      connectionIds.push(connection.id);
      return connection;
    });

    // Fix the element outputs, make sure they are valid and replace them with new ones
    elementNodes.forEach(element => {
      element.outputs = element.outputs
        .filter(connectionId => has(hash, connectionId))
        .map(connectionId => hash[connectionId]);

      // replace attributes
      if (Array.isArray(element.attributes) && element.attributes.length > 0) {
        element.attributes = element.attributes
          .filter(attributeId => has(hash, attributeId))
          .map(attributeId => hash[attributeId]);
      }
    });

    // Fix the condition outputs, make sure they are valid and replace them with new ones
    conditionNodes.forEach(condition => {
      condition.output = hash[condition.output] || null;
    });

    // Cut-specific operations
    if (clipboard.operation === OPERATION.CUT) {
      // Fix the jumper references when elements are cut, replace them with the new elements
      Object.entries(jumperReferences).forEach(([jumperId, elementId]) => {
        elementId = hash[elementId];
        context.dispatch(
          'project/data/jumpers/customUpdate',
          {
            id: jumperId,
            props: { elementId },
            apply: false,
          },
          { root: true }
        );
      });
      // Restore the starting element with the new element
      if (startingElement) {
        const newStartingElement = hash[startingElement];
        if (newStartingElement) {
          context.dispatch(
            'project/data/elements/setStartingElement',
            {
              id: newStartingElement,
              apply: false,
            },
            { root: true }
          );
        }
      }

      // Create the replies to the threads
      const commentChildrenNodes = commentChildren.map(comment => {
        const id = uuidv4();
        hash[comment.id] = id;
        const node = _.cloneDeep(comment);
        node.id = id;
        return node;
      });

      // Create the threads
      const commentIds = [];
      const commentNodes = comments.map(comment => {
        const id = uuidv4();
        hash[comment.id] = id;
        const node = _.cloneDeep(comment);
        node.id = id;
        node.position.x -= diff.x;
        node.position.y -= diff.y;
        // Adjust x, y when snapping to grid
        if (snap) {
          node.position.x = roundToNearestMultiple(node.position.x, size);
          node.position.y = roundToNearestMultiple(node.position.y, size);
        }
        node.position.id = boardId;
        node.children = [];

        commentIds.push(id);
        return node;
      });

      const root =
        context.rootGetters['project/data/comments/getRootContainer'].id;
      // Create the threads
      context.dispatch(
        'project/data/comments/createMany',
        {
          nodes: commentNodes,
          tId: root,
          tIndex: '-',
          apply: false,
        },
        { root: true }
      );

      // Create the replies
      comments.forEach(comment => {
        const tId = hash[comment.id];
        const childrenNodes = comment.children.map(childId =>
          commentChildrenNodes.find(node => node.id === hash[childId])
        );
        context.dispatch(
          'project/data/comments/createMany',
          {
            nodes: childrenNodes,
            tId,
            tIndex: '-',
            apply: false,
          },
          { root: true }
        );
      });

      // Create the references to the threads from boards
      context.dispatch(
        'project/data/comments/createReferences',
        {
          ids: commentIds,
          rId: boardId,
          rType: 'boards',
          rProp: 'comments',
          apply: false,
        },
        { root: true }
      );

      // Select threads
      context.dispatch(
        'project/config/comments/selectMany',
        { ids: commentIds, apply: false },
        { root: true }
      );
    }

    console.log('ElementNodes to create from copy:', elementNodes);
    // Create the new elements
    context.dispatch(
      'project/data/elements/createMany',
      {
        nodes: elementNodes,
        tId: boardId,
        tIndex: '-',
        apply: false,
      },
      { root: true }
    );

    // Select them
    context.dispatch(
      'project/config/elements/selectMany',
      { ids: elementIds, apply: false },
      { root: true }
    );

    // Create the new attributes
    context.dispatch(
      'project/data/attributes/createMany',
      {
        nodes: attributeNodes,
        tId: null,
        tIndex: null,
        apply: false,
      },
      { root: true }
    );

    // Create the new jumpers
    context.dispatch(
      'project/data/jumpers/createMany',
      {
        nodes: jumperNodes,
        tId: boardId,
        tIndex: '-',
        apply: false,
      },
      { root: true }
    );

    // Select them
    context.dispatch(
      'project/config/jumpers/selectMany',
      { ids: jumperIds, apply: false },
      { root: true }
    );

    // Create new conditions
    context.dispatch(
      'project/data/conditions/createMany',
      {
        nodes: conditionNodes,
        tId: null,
        tIndex: null,
        apply: false,
      },
      { root: true }
    );

    // Create the new branches
    context.dispatch(
      'project/data/branches/createMany',
      {
        nodes: branchNodes,
        tId: boardId,
        tIndex: '-',
        apply: false,
      },
      { root: true }
    );

    // Select them
    context.dispatch(
      'project/config/branches/selectMany',
      { ids: branchIds, apply: false },
      { root: true }
    );

    // Create the new notes
    context.dispatch(
      'project/data/notes/createMany',
      {
        nodes: noteNodes,
        tId: boardId,
        tIndex: '-',
        apply: false,
      },
      { root: true }
    );

    // Select them
    context.dispatch(
      'project/config/notes/selectMany',
      { ids: noteIds, apply: false },
      { root: true }
    );

    // Create the new connections
    context.dispatch(
      'project/data/connections/createMany',
      {
        nodes: connectionNodes,
        tId: boardId,
        tIndex: '-',
        apply: false,
      },
      { root: true }
    );

    // Select them
    context.dispatch(
      'project/config/connections/selectMany',
      { ids: connectionIds, apply: false },
      { root: true }
    );

    updater.complete({ apply: true });

    // In case the operation was a cut operation empty the clipboard
    if (clipboard.operation === OPERATION.CUT) {
      context.dispatch('empty', {});
    }
  },

  async shiftPaste(context, { atPos }) {
    console.log('Shift Pasting content...');
    context.dispatch(
      'project/config/deselectAll',
      { apply: false },
      { root: true }
    );
    const clipboard = context.getters.getClipboard;
    const elements = _.cloneDeep(clipboard.data.elements);
    const jumpers = _.cloneDeep(clipboard.data.jumpers);
    const branches = _.cloneDeep(clipboard.data.branches);
    const conditions = _.cloneDeep(clipboard.data.conditions);

    const isSameProject =
      clipboard.hash === context.rootGetters['user/projects/getOpen'];

    const SHIFT_PASTE_TYPES = {
      PASTE_AS_JUMPER: 'PASTE_AS_JUMPER',
      NOT_CONFIGURED: undefined,
    };
    let pasteType = SHIFT_PASTE_TYPES.NOT_CONFIGURED;

    if (
      elements.length === 1 &&
      jumpers.length === 0 &&
      branches.length === 0 &&
      conditions.length === 0
    ) {
      pasteType = SHIFT_PASTE_TYPES.PASTE_AS_JUMPER;
    }

    if (pasteType === SHIFT_PASTE_TYPES.NOT_CONFIGURED) {
      return;
    }

    const boardId = context.rootGetters['project/config/boards/getOpen'];

    if (pasteType === SHIFT_PASTE_TYPES.PASTE_AS_JUMPER) {
      const selectedJumperIds =
        context.rootGetters['project/config/jumpers/getSelections'];

      if (selectedJumperIds.length === 1) {
        // Update selected jumper
        context.dispatch(
          'project/data/jumpers/customUpdate',
          {
            id: selectedJumperIds[0],
            props: {
              elementId: elements[0].id,
            },
            apply: false,
          },
          { root: true }
        );

        context.dispatch(
          'project/config/jumpers/select',
          { id: selectedJumperIds[0], apply: false },
          { root: true }
        );
      } else {
        // Create new jumper
        const jumperId = uuidv4();
        context.dispatch(
          'project/data/jumpers/customCreate',
          {
            boardId,
            id: jumperId,
            position: { x: atPos.x, y: atPos.y },
            props: {
              elementId: isSameProject ? elements[0].id : null,
            },
            apply: false,
          },
          { root: true }
        );

        context.dispatch(
          'project/config/jumpers/selectMany',
          { ids: [jumperId], apply: false },
          { root: true }
        );
      }
    }

    updater.complete({ apply: true });
  },

  /**
   * Update the mentions for a reference in the clipboard
   * @param {String}  id    The reference id
   * @param {String}  type  The reference type
   * @param {String}  name  The updated name of the mention
   * @param {String}  label The original label of the mention if different from name
   */
  updateMentions(context, { id, type, name, label, apply }) {
    const data = _.cloneDeep(context.state.data);
    Object.values(data.elements).forEach(element => {
      element.content = setMentionContent(
        element.content,
        id,
        type,
        name,
        label
      );
      element.title = setMentionContent(element.title, id, type, name, label);
    });
    Object.values(data.attributes).forEach(attribute => {
      if (attribute.value.type === 'string' && !attribute.value.plain) {
        attribute.value.data = setMentionContent(
          attribute.value.data,
          id,
          type,
          name,
          label
        );
      }
    });
    Object.values(data.connections).forEach(connection => {
      connection.label = setMentionContent(
        connection.label,
        id,
        type,
        name,
        label
      );
    });
    Object.values(data.notes).forEach(note => {
      note.content = setMentionContent(note.content, id, type, name, label);
    });
    if (Array.isArray(data?.conditions)) {
      Object.values(data.conditions).forEach(condition => {
        condition.script = setMentionContent(
          condition.script,
          id,
          type,
          name,
          label
        );
      });
    }
    // Replace the clipboard content with the updated mentions
    context.commit('setClipboard', {
      data,
      type: context.state.type,
      operation: context.state.operation,
      hash: context.state.hash,
    });
  },

  /**
   * Delete the mentions for a reference in the clipboard
   * @param {String}  ids     The mention ids
   * @param {String}  type    The mention type
   */
  deleteMentions(context, { ids, type }) {
    const data = _.cloneDeep(context.state.data);

    if (type === 'component') {
      // Remove the component from any component list
      Object.values(data.elements)
        .filter(
          element =>
            has(element, 'components') &&
            element.components.some(c => ids.includes(c.component))
        )
        .forEach(element => {
          element.components = element.components.filter(
            c => !ids.includes(c.component)
          );
        });
    } else if (type === 'board') {
      // Remove linked board
      Object.values(data.elements)
        .filter(
          element =>
            has(element, 'linkedBoard') && ids.includes(element.linkedBoard)
        )
        .forEach(element => {
          if (element.linkedBoard) delete element.linkedBoard;
        });
    }
    console.log('Delete Mentions called');
    Object.values(data.attributes).forEach(attribute => {
      console.log(`In attribute: ${attribute.id}`);
      // Rich text attributes
      if (attribute.value.type === 'string' && !attribute.value.plain) {
        attribute.value.data = unwrapMention(attribute.value.data, ids, type);
      }
      if (type === 'component' && attribute.value.type === 'component-list') {
        attribute.value.data = attribute.value.data.filter(
          c => !ids.includes(c.component)
        );
      }
    });
    Object.values(data.elements).forEach(element => {
      element.content = unwrapMention(element.content, ids, type);
      element.title = unwrapMention(element.title, ids, type);
    });
    Object.values(data.connections).forEach(connection => {
      connection.label = unwrapMention(connection.label, ids, type);
    });
    Object.values(data.notes).forEach(note => {
      note.content = unwrapMention(note.content, ids, type);
    });
    if (Array.isArray(data?.conditions)) {
      Object.values(data.conditions).forEach(condition => {
        condition.script = unwrapMention(condition.script, ids, type);
      });
    }
    // Replace the clipboard content with the deleted mentions
    context.commit('setClipboard', {
      data,
      type: context.state.type,
      operation: context.state.operation,
      hash: context.state.hash,
    });
  },

  /**
   * Deletes the element targets for the jumpers in clipboard
   * @param {object} context
   * @param {object} param1
   * @param {Array} param1.elementIds
   */
  deleteElementsFromJumpers(context, { elementIds }) {
    const data = _.cloneDeep(context.state.data);
    Object.values(data.jumpers).forEach(jumper => {
      if (elementIds.includes(jumper.elementId)) {
        jumper.elementId = null;
      }
    });

    context.commit('setClipboard', {
      data,
      type: context.state.type,
      operation: context.state.operation,
      hash: context.state.hash,
    });
  },

  /**
   * Clean the clipboard
   */
  empty(context) {
    context.commit('setClipboard', {
      data: {
        elements: [],
        attributes: [],
        notes: [],
        jumpers: [],
        branches: [],
        connections: [],
      },
      type: null,
      operation: null,
      hash: null,
    });
  },

  /**
   * Set the clipboard to a specific value
   */
  setClipboard(context, { data, type, operation, hash }) {
    context.commit('setClipboard', { data, type, operation, hash });
  },
};

const mutations = {
  /**
   * @param {Array}     data        The clipboard data
   * @param {ELEMENTS}  type        The type of the data that is put in the application clipboard
   * @param {CUT|COPY}  operation   The operation being made (cut, copy)
   * @param {String}    hash        The project that will have the operations
   */
  setClipboard(state, { data, type, operation, hash }) {
    state.data = data;
    state.type = type;
    state.operation = operation;
    state.hash = hash;
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
